import React, { useEffect, useMemo, useRef, useState } from "react";
import { updateCombat } from "../services/combatsServices";
import { toast } from "react-toastify";

/**
 * EditCombatModal (v2)
 * - Résultats stricts : V / D / N / H / F
 * - Saisie par scores : Ippon / Waza-ari / Yuko / Shido (Shido = 0 point)
 * - details_combat auto-généré (read-only)
 * - Rouge/Blanc avec nom + prénom
 * - Refresh combats distribution après save
 */

const WEIGHTS = {
  ippon: 10,
  wazari: 7,
  yuko: 5,
  shido: 0, // ✅ pénalité => 0 pt
};

const safeInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.trunc(n));
};

const computePoints = (s) =>
  safeInt(s.ippon) * WEIGHTS.ippon +
  safeInt(s.wazari) * WEIGHTS.wazari +
  safeInt(s.yuko) * WEIGHTS.yuko +
  safeInt(s.shido) * WEIGHTS.shido;

const sanitizeCount = (val, max = 99) => {
  const digits = String(val ?? "")
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");
  if (digits === "") return "0";
  const n = Math.min(parseInt(digits, 10), max);
  return String(n);
};

const buildSideLine = (name, s) => {
  const pts = computePoints(s);
  return `${name} — ${safeInt(s.ippon)} Ippon — ${safeInt(
    s.wazari,
  )} Waza-ari — ${safeInt(s.yuko)} Yuko — ${safeInt(
    s.shido,
  )} Shido (${pts} pts)`;
};

const decisionLabelFromCodes = (r1, r2, decision) => {
  if (r1 === "H" || r2 === "H") return "Hansoku-make";
  if (r1 === "F" || r2 === "F") return "Forfait";
  if (r1 === "N" && r2 === "N") return "Égalité";
  return (decision || "Points").trim();
};

const winnerNameFromCodes = (r1, r2, name1, name2) => {
  // V/D
  if (r1 === "V" && r2 === "D") return name1;
  if (r1 === "D" && r2 === "V") return name2;

  // Hansoku / Forfait : celui qui n’a pas H/F gagne
  if (r1 === "H" && r2 === "V") return name2;
  if (r2 === "H" && r1 === "V") return name1;

  if (r1 === "F" && r2 === "V") return name2;
  if (r2 === "F" && r1 === "V") return name1;

  return ""; // N
};

const buildDetailsCombat = ({ r1, r2, decision, name1, name2, s1, s2 }) => {
  const sideA = buildSideLine(name1, s1);
  const sideB = buildSideLine(name2, s2);

  if (r1 === "N" && r2 === "N") {
    return `Égalité • ${sideA} / ${sideB}`;
  }

  const winner = winnerNameFromCodes(r1, r2, name1, name2);
  const decisionLabel = decisionLabelFromCodes(r1, r2, decision);

  return `Gagnant : ${winner} • ${decisionLabel} | ${sideA} / ${sideB}`;
};

const normalizeText = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const DEFAULT_COUNTS = { ippon: "0", wazari: "0", yuko: "0", shido: "0" };

const parseSideLine = (line) => {
  const text = String(line || "");

  const get = (labelRegex) => {
    const m = text.match(new RegExp(`(\\d+)\\s*${labelRegex}`, "i"));
    return m ? String(m[1]) : "0";
  };

  return {
    ippon: get("Ippon"),
    wazari: get("Waza\\s*[--–—]?\\s*ari"),
    yuko: get("Yuko"),
    shido: get("Shido"),
  };
};

const extractEntriesFromDetails = (details) => {
  const text = String(details || "");
  if (!text) return [];

  // On prend ce qui est après le dernier "|" si présent
  const afterPipe = text.includes("|") ? text.split("|").pop() : text;

  // Format attendu : "SIDE_A / SIDE_B"
  const parts = afterPipe
    .split(" / ")
    .map((s) => s.trim())
    .filter(Boolean);

  return parts.map((p) => {
    // nom affiché = tout avant le "—" (em dash)
    const namePart = p.split("—")[0]?.trim() || "";
    return { name: namePart, counts: parseSideLine(p) };
  });
};

const pickCountsForCompetiteur = (entries, competiteur, fallbackIndex = 0) => {
  if (!entries?.length) return null;

  const nom = normalizeText(competiteur?.nom_competiteur);
  const prenom = normalizeText(competiteur?.prenom_competiteur);

  const matchBoth = (e) => {
    const n = normalizeText(e.name);
    // ordre indifférent : on exige juste que les deux tokens soient présents
    return !!nom && n.includes(nom) && !!prenom && n.includes(prenom);
  };

  const matchNomOnly = (e) => {
    const n = normalizeText(e.name);
    return !!nom && n.includes(nom);
  };

  let hit = entries.find(matchBoth);
  if (!hit) hit = entries.find(matchNomOnly);
  if (!hit) hit = entries[fallbackIndex] || entries[0];

  return hit?.counts || null;
};

const parseCountsFromDetails = (details, competiteur, fallbackIndex = 0) => {
  const entries = extractEntriesFromDetails(details);
  return pickCountsForCompetiteur(entries, competiteur, fallbackIndex);
};

const resultLabels = {
  V: "Victoire",
  D: "Défaite",
  N: "Nul",
  F: "Forfait",
  H: "Hansoku-make",
};

const SegmentedResults = ({ value, onChange, disabled = false }) => (
  <div
    className={[
      "inline-flex rounded-lg border border-gray-300 overflow-hidden shadow-sm",
      disabled ? "opacity-60 pointer-events-none" : "",
    ].join(" ")}
  >
    {Object.entries(resultLabels).map(([key, label], idx, arr) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        title={label}
        className={[
          "px-3 py-2 text-sm font-medium transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
          value === key
            ? "bg-blue-600 text-white"
            : "bg-white text-gray-700 hover:bg-gray-100",
          idx !== arr.length - 1 ? "border-r border-gray-300" : "",
        ].join(" ")}
      >
        {key}
      </button>
    ))}
  </div>
);

// normalisation des couples (r1, r2) pour rester cohérent
const syncFromRed = (r) => {
  switch (r) {
    case "V":
      return { r1: "V", r2: "D" };
    case "D":
      return { r1: "D", r2: "V" };
    case "N":
      return { r1: "N", r2: "N" };
    case "H":
      return { r1: "H", r2: "V" };
    case "F":
      return { r1: "F", r2: "V" };
    default:
      return { r1: "", r2: "" };
  }
};

const syncFromWhite = (r) => {
  switch (r) {
    case "V":
      return { r1: "D", r2: "V" };
    case "D":
      return { r1: "V", r2: "D" };
    case "N":
      return { r1: "N", r2: "N" };
    case "H":
      return { r1: "V", r2: "H" };
    case "F":
      return { r1: "V", r2: "F" };
    default:
      return { r1: "", r2: "" };
  }
};

const EditCombatModal = ({
  isOpen,
  onClose,
  competiteur1,
  competiteur2,
  initialData,
  distributionId,
  position1,
  position2,
  refreshPouleTable,
  refreshCombats, // ✅ optionnel : refetch combats distribution
}) => {
  // Résultats stricts (V D N H F)
  const [resultatCompetiteur1, setResultatCompetiteur1] = useState("");
  const [resultatCompetiteur2, setResultatCompetiteur2] = useState("");

  // Décision affichée dans details_combat (ex: Points / Hantei / Ippon...)
  const [decision, setDecision] = useState("Points");

  // Compteurs score (string contrôlée)
  const [s1, setS1] = useState({
    ippon: "0",
    wazari: "0",
    yuko: "0",
    shido: "0",
  });
  const [s2, setS2] = useState({
    ippon: "0",
    wazari: "0",
    yuko: "0",
    shido: "0",
  });

  const initKeyRef = useRef(null); // évite de réinitialiser pendant la saisie

  const name1 = useMemo(() => {
    const nom = (competiteur1?.nom_competiteur || "").trim();
    const prenom = (competiteur1?.prenom_competiteur || "").trim();
    const n = `${nom} ${prenom}`.trim();
    return n || (competiteur1?.id ? `#${competiteur1.id}` : "Rouge");
  }, [competiteur1]);

  const name2 = useMemo(() => {
    const nom = (competiteur2?.nom_competiteur || "").trim();
    const prenom = (competiteur2?.prenom_competiteur || "").trim();
    const n = `${nom} ${prenom}`.trim();
    return n || (competiteur2?.id ? `#${competiteur2.id}` : "Blanc");
  }, [competiteur2]);

  // Remplissage à l'ouverture (1 seule fois par combat)
  useEffect(() => {
    if (!isOpen) return;
    const key = `${initialData?.id || "noid"}:${initialData?.updatedAt || ""}`;
    if (initKeyRef.current === key) return;
    initKeyRef.current = key;

    const r1 = initialData?.resultat_competiteur1 || "";
    const r2 = initialData?.resultat_competiteur2 || "";

    setResultatCompetiteur1(r1);
    setResultatCompetiteur2(r2);

    // décision par défaut
    setDecision("Points");

    // préfill scores via parsing details si possible, sinon 0
    const parsed1 = parseCountsFromDetails(
      initialData?.details_combat,
      competiteur1,
      0,
    );
    const parsed2 = parseCountsFromDetails(
      initialData?.details_combat,
      competiteur2,
      1,
    );

    setS1(parsed1 || DEFAULT_COUNTS);
    setS2(parsed2 || DEFAULT_COUNTS);
  }, [isOpen, initialData, name1, name2]);

  const setRedResult = (r) => {
    const { r1, r2 } = syncFromRed(r);
    setResultatCompetiteur1(r1);
    setResultatCompetiteur2(r2);
  };

  const setWhiteResult = (r) => {
    const { r1, r2 } = syncFromWhite(r);
    setResultatCompetiteur1(r1);
    setResultatCompetiteur2(r2);
  };

  const points1 = useMemo(() => computePoints(s1), [s1]);
  const points2 = useMemo(() => computePoints(s2), [s2]);

  const isAutoDecision =
    resultatCompetiteur1 === "N" ||
    resultatCompetiteur2 === "N" ||
    resultatCompetiteur1 === "H" ||
    resultatCompetiteur2 === "H" ||
    resultatCompetiteur1 === "F" ||
    resultatCompetiteur2 === "F";

  const effectiveDecision = useMemo(
    () =>
      decisionLabelFromCodes(
        resultatCompetiteur1,
        resultatCompetiteur2,
        decision,
      ),
    [resultatCompetiteur1, resultatCompetiteur2, decision],
  );

  const computedDetails = useMemo(() => {
    if (!resultatCompetiteur1 || !resultatCompetiteur2) return "";
    return buildDetailsCombat({
      r1: resultatCompetiteur1,
      r2: resultatCompetiteur2,
      decision: effectiveDecision,
      name1,
      name2,
      s1,
      s2,
    });
  }, [
    resultatCompetiteur1,
    resultatCompetiteur2,
    effectiveDecision,
    name1,
    name2,
    s1,
    s2,
  ]);

  const refreshAll = async () => {
    // ✅ refresh combats distribution (ou table)
    await refreshPouleTable?.();
    await refreshCombats?.();
  };

  const handleSubmit = async () => {
    try {
      // garde-fou strict
      const allowed = new Set(["V", "D", "N", "H", "F"]);
      if (
        !allowed.has(resultatCompetiteur1) ||
        !allowed.has(resultatCompetiteur2)
      ) {
        toast.error("Résultat invalide (V/D/N/H/F uniquement).");
        return;
      }

      const combatData = {
        competiteur_id1: competiteur1.id,
        competiteur_id2: competiteur2.id,
        distribution_id: distributionId,

        // ✅ uniquement V/D/N/H/F
        resultat_competiteur1: resultatCompetiteur1,
        resultat_competiteur2: resultatCompetiteur2,

        // ✅ points calculés (Shido = 0)
        points_competiteur1: points1,
        points_competiteur2: points2,

        // ✅ texte auto
        details_combat: computedDetails || null,
      };

      await updateCombat(initialData.id, combatData);
      toast.success("Combat mis à jour ✅");

      await refreshAll();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du combat :", error);
      toast.error("Erreur lors de la mise à jour du combat.");
    }
  };

  if (!isOpen || !competiteur1 || !competiteur2) return null;

  const ScoreInputs = ({ scores, setScores, theme }) => {
    const labelCls =
      theme === "red" ? "text-red-900" : "text-gray-700 dark:text-gray-200";

    const inputCls =
      theme === "red"
        ? "border-red-200 focus:ring-red-400"
        : "border-gray-300 dark:border-gray-600 focus:ring-blue-400";

    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["ippon", "Ippon"],
          ["wazari", "Waza-ari"],
          ["yuko", "Yuko"],
          ["shido", "Shido"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className={`block text-xs font-semibold mb-1 ${labelCls}`}>
              {label}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={scores[k]}
              onChange={(e) =>
                setScores((prev) => ({
                  ...prev,
                  [k]: sanitizeCount(e.target.value, 99),
                }))
              }
              className={[
                "w-full rounded-lg bg-white dark:bg-gray-700 px-3 py-2 text-sm",
                "text-slate-900 dark:text-white outline-none border",
                "focus:ring-2",
                inputCls,
              ].join(" ")}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="w-full max-w-4xl rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* En-tête */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white dark:from-gray-900 dark:to-gray-900">
          <h2 className="text-lg md:text-xl font-bold text-slate-800 dark:text-slate-100">
            Modifier le combat
          </h2>
          <button
            onClick={onClose}
            className="rounded-full w-9 h-9 flex items-center justify-center bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:text-slate-300 dark:hover:bg-gray-700"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Corps */}
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rouge (compétiteur 1) */}
            <div className="rounded-xl border border-red-200 bg-red-50/70 shadow-sm">
              <div className="px-4 py-3 border-b border-red-200 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-600 text-white">
                    Rouge
                  </span>
                  <p className="text-sm font-semibold text-red-900 truncate">
                    {position1} — {competiteur1.nom_competiteur}{" "}
                    {competiteur1.prenom_competiteur}
                  </p>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-red-900 mb-1">
                    Résultat (V/D/N/H/F)
                  </label>
                  <SegmentedResults
                    value={resultatCompetiteur1}
                    onChange={setRedResult}
                  />
                  <div className="mt-1 text-[11px] text-red-800/80">
                    Le choix est synchronisé automatiquement avec le Blanc.
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-red-900 mb-2">
                    Scores
                  </label>
                  <ScoreInputs scores={s1} setScores={setS1} theme="red" />
                  <div className="mt-2 text-sm text-red-900 font-semibold">
                    Total points : {points1}{" "}
                    <span className="text-xs font-normal">(Shido = 0)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Blanc (compétiteur 2) */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-800">
                    Blanc
                  </span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {position2} — {competiteur2.nom_competiteur}{" "}
                    {competiteur2.prenom_competiteur}
                  </p>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                    Résultat (V/D/N/H/F)
                  </label>
                  <SegmentedResults
                    value={resultatCompetiteur2}
                    onChange={setWhiteResult}
                  />
                  <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">
                    Le choix est synchronisé automatiquement avec le Rouge.
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Scores
                  </label>
                  <ScoreInputs scores={s2} setScores={setS2} theme="white" />
                  <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 font-semibold">
                    Total points : {points2}{" "}
                    <span className="text-xs font-normal">(Shido = 0)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Décision + Preview details_combat */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                Décision (affichage)
              </label>
              <input
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                disabled={isAutoDecision}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
                placeholder="Ex: Points / Hantei / Ippon..."
              />
              {isAutoDecision && (
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Auto : {effectiveDecision}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                details_combat (auto-généré)
              </label>
              <textarea
                readOnly
                rows={3}
                value={computedDetails}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900/40 px-3 py-2 text-sm text-slate-900 dark:text-white"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
              disabled={!resultatCompetiteur1 || !resultatCompetiteur2}
              title={
                !resultatCompetiteur1 || !resultatCompetiteur2
                  ? "Choisissez un résultat (V/D/N/H/F)"
                  : ""
              }
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCombatModal;
